import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  fetchAdminUser,
  updateAdminUser,
  resetAdminUserPassword,
  type AdminUser,
} from '../services/admin';
import { Analytics as AdAnalytics } from '../components/admin/Analytics';
import { listVideos, bulkAssignAdsToUser } from '../api/videos';
import PaymentHistory from '../components/payments/PaymentHistory';

const isLikelyObjectId = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-f\d]{24}$/i.test(value.trim());

const AdminUserDetailPage: React.FC = () => {
  const params = useParams();
  const userId = params.id as string | undefined;

  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [availableAds, setAvailableAds] = useState<{ _id: string; title: string }[]>([]);
  const [adsLoading, setAdsLoading] = useState<boolean>(true);
  const [adsError, setAdsError] = useState<string | null>(null);
  const [selectedAdIds, setSelectedAdIds] = useState<string[]>([]);
  const [assigningAds, setAssigningAds] = useState<boolean>(false);
  const [assignAdsStatus, setAssignAdsStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setError('Missing user id');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminUser(userId);
        if (!cancelled) {
          setUser(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Failed to load user';
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;

    const loadAds = async () => {
      setAdsLoading(true);
      setAdsError(null);
      try {
        const data = await listVideos({
          includeMeta: false,
          pageSize: 50,
          sortBy: 'createdAt',
          sortDir: 'desc',
        });
        if (!cancelled) {
          const rawItems: unknown = Array.isArray(data)
            ? data
            : Array.isArray((data as { items?: unknown }).items)
              ? (data as { items?: unknown }).items
              : [];

          const typedItems = Array.isArray(rawItems)
            ? (rawItems as { _id?: unknown; title?: unknown }[])
            : [];

          setAvailableAds(
            typedItems
              .map((ad) => ({
                _id: String(ad._id ?? ''),
                title: typeof ad.title === 'string' ? ad.title : 'Untitled ad',
              }))
              .filter((ad) => isLikelyObjectId(ad._id)),
          );
        }
      } catch {
        if (!cancelled) {
          setAdsError('Failed to load ads');
        }
      } finally {
        if (!cancelled) {
          setAdsLoading(false);
        }
      }
    };

    void loadAds();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !user) return;

    setSaving(true);
    setError(null);
    try {
      const updated = await updateAdminUser(userId, {
        username: user.username,
        email: user.email,
      });
      setUser(updated);
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
          ? ((err as { message?: unknown }).message as string)
          : 'Failed to update user';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setResetting(true);
    setError(null);
    try {
      await resetAdminUserPassword(userId, newPassword);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
          ? ((err as { message?: unknown }).message as string)
          : 'Failed to reset password';
      setError(msg);
    } finally {
      setResetting(false);
    }
  };

  const toggleAdSelection = (adId: string) => {
    setSelectedAdIds((prev) =>
      prev.includes(adId) ? prev.filter((id) => id !== adId) : [...prev, adId],
    );
  };

  const handleBulkAssignAds = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLikelyObjectId(userId)) {
      setAssignAdsStatus('This user does not have a valid account id for ad assignment.');
      return;
    }

    const validSelectedAdIds = selectedAdIds.filter((id) => isLikelyObjectId(id));
    if (!validSelectedAdIds.length) {
      setAssignAdsStatus('Select at least one ad to assign');
      return;
    }

    setAssigningAds(true);
    setAssignAdsStatus(null);
    try {
      await bulkAssignAdsToUser({ adIds: validSelectedAdIds, userId });
      setAssignAdsStatus('Assigned ads to user successfully.');
      setSelectedAdIds([]);
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
          ? ((err as { message?: unknown }).message as string)
          : 'Failed to assign ads to user';
      setAssignAdsStatus(msg);
    } finally {
      setAssigningAds(false);
    }
  };

  const adsHelpText = useMemo(() => {
    if (adsLoading) return 'Loading ads…';
    if (adsError) return adsError;
    if (!availableAds.length) return 'No ads are available yet to assign to this user.';
    return 'Select one or more ads below and assign them to this user profile.';
  }, [adsLoading, adsError, availableAds.length]);

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 space-y-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">User profile</h1>
          {user && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {user.username || user.email}  b7 {user.email}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/user-management"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Back to users
          </Link>
          <Link
            to="/"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go home
          </Link>
        </div>
      </header>

      {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading user…</p>}
      {error && !loading && <p className="text-sm text-red-600">{error}</p>}

      {user && !loading && !error && (
        <section className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Profile details</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Edit basic details for this user. Role and account status controls remain in the main
              User Management table.
            </p>
            <form
              onSubmit={handleSave}
              className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 text-sm text-slate-800 dark:text-slate-200 md:grid-cols-2"
            >
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400">
                  User ID
                  <div className="mt-0.5 break-all rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-2 py-1 text-[11px] text-slate-600 dark:text-slate-400">
                    {user.id}
                  </div>
                </label>
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400">
                  Name
                  <input
                    type="text"
                    value={user.username || ''}
                    onChange={(e) =>
                      setUser({
                        ...user,
                        username: e.target.value,
                      })
                    }
                    className="mt-0.5 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm text-slate-800 dark:text-slate-200 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400">
                  Email
                  <input
                    type="email"
                    value={user.email}
                    onChange={(e) =>
                      setUser({
                        ...user,
                        email: e.target.value,
                      })
                    }
                    className="mt-0.5 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm text-slate-800 dark:text-slate-200 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Role</div>
                <div className="mt-0.5 text-sm capitalize text-slate-800 dark:text-slate-200">{user.role}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Account status</div>
                <div className="mt-0.5 text-sm capitalize text-slate-800 dark:text-slate-200">
                  {user.accountStatus || 'good'}
                </div>
              </div>
              <div className="md:col-span-2 mt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>

          <div className="rounded-xl border border-red-100 bg-red-50 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-red-800">Reset password</h2>
            <p className="mt-1 text-xs text-red-700">
              Set a new password for this user. They will need to use the new password the next time
              they sign in.
            </p>
            <form
              onSubmit={handleResetPassword}
              className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2"
            >
              <label className="block text-xs text-red-800">
                New password
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-0.5 w-full rounded-md border border-red-200 bg-white dark:bg-slate-900 px-2 py-1 text-sm text-slate-900 dark:text-slate-100 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              </label>
              <label className="block text-xs text-red-800">
                Confirm password
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-0.5 w-full rounded-md border border-red-200 bg-white dark:bg-slate-900 px-2 py-1 text-sm text-slate-900 dark:text-slate-100 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              </label>
              <div className="md:col-span-2 mt-1 flex justify-end">
                <button
                  type="submit"
                  disabled={resetting}
                  className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {resetting ? 'Resetting…' : 'Reset password'}
                </button>
              </div>
            </form>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Assign ads to this user</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{adsHelpText}</p>
            {availableAds.length > 0 && !adsLoading && !adsError && (
              <form onSubmit={handleBulkAssignAds} className="mt-3 space-y-2 text-sm">
                <div className="max-h-48 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-2 py-1">
                  {availableAds.map((ad) => (
                    <label
                      key={ad._id}
                      className="flex cursor-pointer items-center gap-2 border-b border-slate-100 py-1 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAdIds.includes(ad._id)}
                        onChange={() => toggleAdSelection(ad._id)}
                        className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs text-slate-800 dark:text-slate-200 line-clamp-1">{ad.title}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 dark:text-slate-400">
                    {selectedAdIds.length} ad{selectedAdIds.length === 1 ? '' : 's'} selected
                  </span>
                  <button
                    type="submit"
                    disabled={assigningAds || !selectedAdIds.length}
                    className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  >
                    {assigningAds ? 'Assigning…' : 'Assign selected ads'}
                  </button>
                </div>
                {assignAdsStatus && <p className="text-[11px] text-slate-600 dark:text-slate-400">{assignAdsStatus}</p>}
              </form>
            )}
          </div>

          <div id="payment-history" className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <PaymentHistory
              userId={user.id}
              title="Payment history"
              subtitle="Digital footprint of prior transactions linked to this account."
            />
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Business analytics</h2>
            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
              Performance for all ads associated with this business account.
            </p>
            <AdAnalytics userId={user.id} title="Business analytics" />
          </div>
        </section>
      )}
    </main>
  );
};

export default AdminUserDetailPage;
